import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { VENDOR_FILTERS } from "@/data/content";
import { Eyebrow, Lotus } from "./Ornaments";
import { Reveal } from "./Reveal";
import { Field } from "./Field";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { business_name: "", contact_person: "", category: "", city: "", phone: "", email: "", portfolio: "", about: "" };
const PERKS = ["Qualified leads from real families", "Featured in our vendor gallery", "Clear briefs & on-time payments"];

export const VendorRegister = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/vendor-registrations`, form);
      toast.success("Welcome aboard! We'll review your profile and get in touch.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(err?.response?.status === 422 ? "Please check your email and phone details." : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="join" data-testid="vendor-register-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="rounded-[40px] bg-[#EEF1E7] p-6 sm:p-12 lg:p-16 grid lg:grid-cols-12 gap-10 relative overflow-hidden">
          <Lotus className="absolute -right-10 -bottom-6 w-72 h-48 opacity-30" color="#8E9D78" />
          <Reveal className="lg:col-span-5 relative">
            <Eyebrow>Vendor registration</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl text-ink mt-5 leading-[1.05]">Grow with <span className="italic text-sage-deep">Nexora.</span></h2>
            <p className="text-base md:text-lg text-muted-ink mt-6 leading-relaxed">Decorators, photographers, caterers, mehendi artists, venues, pandits and performers — join our curated vendor family.</p>
            <ul className="mt-8 space-y-4">
              {PERKS.map((p) => (
                <li key={p} className="flex items-center gap-3 text-ink"><Sparkles size={16} className="text-gold" />{p}</li>
              ))}
            </ul>
            <div className="mt-10 hidden lg:block rounded-[24px] overflow-hidden aspect-[16/10]">
              <img src="/images/decor.jpg" alt="Wedding decor archway" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7 relative">
            <form onSubmit={submit} data-testid="vendor-register-form" className="bg-white rounded-[32px] p-6 sm:p-10 grid sm:grid-cols-2 gap-5">
              <Field label="Business name" name="business_name" required value={form.business_name} onChange={set} testid="vendor-business-input" />
              <Field label="Contact person" name="contact_person" required value={form.contact_person} onChange={set} testid="vendor-contact-input" />
              <Field label="Category" name="category" as="select" options={VENDOR_FILTERS.slice(1).concat(["Entertainment", "Pandit / Spiritual", "Other"])} required value={form.category} onChange={set} testid="vendor-category-select" />
              <Field label="City" name="city" required value={form.city} onChange={set} testid="vendor-city-input" />
              <Field label="Phone" name="phone" type="tel" required value={form.phone} onChange={set} testid="vendor-phone-input" />
              <Field label="Email" name="email" type="email" required value={form.email} onChange={set} testid="vendor-email-input" />
              <div className="sm:col-span-2">
                <Field label="Instagram / portfolio link" name="portfolio" value={form.portfolio} onChange={set} placeholder="https://" testid="vendor-portfolio-input" />
              </div>
              <div className="sm:col-span-2">
                <Field label="About your work" name="about" as="textarea" value={form.about} onChange={set} testid="vendor-about-input" />
              </div>
              <button type="submit" disabled={loading} data-testid="vendor-register-button" className="btn-sage sm:col-span-2 justify-center py-4 text-base disabled:opacity-70">
                {loading && <Loader2 size={18} className="animate-spin" />} Register as a vendor
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
